<template>
  <div>
    <v-expansion-panels accordion class="tracker-summary">
      <v-expansion-panel>
        <v-expansion-panel-header>
          Workout Block History
          <span v-if="currentExerciseBlock" class="text-right">
            <span class="font-weight-medium">Current:</span>
            <span v-if="currentExerciseBlock.length > 0">
              <span v-for="block in currentExerciseBlock" :key="block.id" class="block">
                {{ block.title }}
              </span>
            </span>
            <span v-else>
              No Active blocks
            </span>
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="blockHeaders"
            :items="blockEvents"
            :items-per-page="5"
            dense
            :loading="loading"
            class="mt-2 elevation-2"
          >
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>
          Nutrition Block History
          <span v-if="currentNutritionBlock" class="text-right">
            <span class="font-weight-medium">Current:</span>
            <span v-if="currentNutritionBlock" class="block">
              {{ currentNutritionBlock.title }}
            </span>
            <span v-else>
              No Active blocks
            </span>
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="headers"
            :items="nutritionBlockEvents"
            :items-per-page="5"
            dense
            :loading="loading"
            class="mt-2 elevation-2"
          >
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Workout History</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="headers"
            :items="activityEvents"
            :items-per-page="5"
            dense
            :loading="loading"
            @click:row="selectRow"
            class="mt-2 elevation-2"
          >
            <template v-slot:[`item.name`]="props">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ props.item.name }}
                  </span>
                </template>
                <span>
                  Effort: {{ props.item.effort }}
                  <br />
                  Calories: {{ props.item.calories }}
                  <br />
                  Heart rate {{ props.item.heartRate }}
                </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header>Nutrition History</v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-data-table
            :headers="headers"
            :items="nutritionEvents"
            :items-per-page="5"
            dense
            :loading="loading"
            @click:row="selectRow"
            class="mt-2 elevation-2"
          >
            <template v-slot:[`item.name`]="props">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ props.item.name }}
                  </span>
                </template>
                <span v-if="props.item.recipe">
                  {{ props.item.recipe.title }}
                </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel class="pa-0">
        <v-expansion-panel-header>Event watch history</v-expansion-panel-header>
        <v-expansion-panel-content class="px-0">
          <v-data-table
            :headers="headers"
            :items="liveEventViewEvents"
            :items-per-page="5"
            dense
            :loading="loading"
            class="mt-2 elevation-2 mx-0"
          >
            <template v-slot:[`item.name`]="props">
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on">
                    {{ props.item.name }}
                  </span>
                </template>
                <span>
                  {{ props.item.eventType.category }}
                  <br />
                  {{ props.item.instructor }}
                </span>
              </v-tooltip>
            </template>
          </v-data-table>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
    <v-dialog v-model="modals.trainingLog" width="900" transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="exercise">
          <v-toolbar-title>Activity Log</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="modals.trainingLog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <weekly-training-log :userId="userId" :selectedDate="selectedDate" />
      </v-card>
    </v-dialog>
    <v-dialog v-model="modals.nutritionLog" persistent width="900" transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar dark color="primary">
          <v-toolbar-title>Nutrition Log</v-toolbar-title>
          <v-spacer />
          <v-btn icon dark @click="modals.nutritionLog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <weekly-nutrition-log :userId="userId" :selectedDate="selectedDate" />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { TRACKER_SUMMARY_QUERY } from "@/graphql/queries/tracker";
import { SUBSCRIPTION_ACTIVITY_QUERY } from "@/constants/graphql/coach/coaching";
import { YYYYMMDD_readable } from "@/lib/fancy_date";
import WeeklyTrainingLog from "@/components/training_log/WeeklyTrainingLog";
import WeeklyNutritionLog from "@/components/nutrition/WeeklyNutritionLog";

export default {
  name: "ClientActivitySummary",
  props: {
    userId: {
      type: String,
      required: true,
    },
    currentExerciseBlock: {
      type: Array,
      required: false,
    },
    currentNutritionBlock: {
      type: Object,
      required: false,
    },
  },
  components: {
    WeeklyNutritionLog,
    WeeklyTrainingLog,
  },
  data() {
    return {
      loading: true,
      activityEvents: [],
      activities: [],
      nutritionEvents: [],
      nutritionBlockEvents: [],
      liveEventViewEvents: [],
      blockEvents: [],
      selectedDate: null,
      modals: {
        trainingLog: null,
        nutritionLog: null,
      },
      blockHeaders: [
        {
          text: "Block type",
          value: "type",
        },
        {
          text: "Block Name",
          value: "name",
        },
        { text: "Start Date", value: "date" },
        { text: "# Dots", value: "logCount" },
      ],
      headers: [
        {
          text: "Activity Name",
          value: "name",
        },
        { text: "Date", value: "date" },
      ],
    };
  },
  methods: {
    selectRow(value) {
      this.selectedDate = value.date;
      if (value.type == "workout") {
        this.modals.trainingLog = true;
      }
      if (value.type == "nutrition") {
        this.modals.nutritionLog = true;
      }
    },
    blockActive(start, end) {
      var startDate = this.$moment(start, "YYYY-MM-DD");
      var endDate = this.$moment(end, "YYYY-MM-DD").subtract(1, "days");

      return this.$moment().isBetween(startDate, endDate, "days", true);
    },
    formatDate(date) {
      return YYYYMMDD_readable(date);
    },
    logItem(activity) {
      var activityItem = {};
      if (activity.workout) {
        activityItem = activity.workout;
      } else if (activity.action) {
        activityItem = activity.action.workout;
      } else if (activity.event) {
        activityItem = activity.event;
      } else if (activity.guidedRun) {
        activityItem = activity.guidedRun;
      }
      return activityItem;
    },
    activityTitle(activity) {
      if (activity.isCustom) {
        if (activity.type) {
          if (activity.type.name.toLowerCase() == "other") {
            return activity.activityType + " | Custom Activity";
          }
          return activity.type.name + " | Custom Activity";
        }
        return "Other | " + activity.activityType.replace("Other: ", "") + " | Custom Activity";
      } else {
        if (activity.event) {
          return activity.event.name;
        } else if (activity.workout || activity.action) {
          return this.logItem(activity).label;
        } else {
          return this.logItem(activity).name;
        }
      }
    },
  },
  apollo: {
    workoutSubscriptionBlocks: {
      query: SUBSCRIPTION_ACTIVITY_QUERY,
      fetchPolicy: "cache-and-network",
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
        };
      },
      result(response) {
        if (response.data) {
          this.loading = false;
          this.blockEvents = response.data.workoutSubscriptionBlocks.edges.map((item) => {
            return {
              date: item.node.startDate,
              name: item.node.block.title,
              type: item.node.block.type,
              logCount: item.node.logCount,
            };
          });

          this.nutritionBlockEvents = response.data.nutritionSubscriptionBlocks.edges.map((item) => {
            return {
              date: item.node.startDate,
              name: item.node.nutritionBlock.title,
            };
          });
        }
      },
    },
    activities: {
      query: TRACKER_SUMMARY_QUERY,
      variables() {
        let encoded_json_payload = btoa("UserNode:" + this.userId);
        return {
          userId: encoded_json_payload,
        };
      },
      fetchPolicy: "cache-and-network",
      result(response) {
        if (response.data) {
          this.loading = false;

          this.activityEvents = response.data.activities.edges.map((item) => {
            return {
              effort: item.node.effort,
              calories: item.node.calories,
              heartRate: item.node.heartRate,
              date: item.node.startDate,
              name: this.activityTitle(item.node),
              type: "workout",
            };
          });

          let workoutLog = response.data.workoutBlockWorkoutLogs.edges.map((item) => {
            return {
              effort: item.node.effort,
              calories: item.node.calories,
              heartRate: item.node.heartRate,
              date: item.node.startDate,
              name: item.node.unitActivity.activity.title,
              type: "workout",
            };
          });

          let restDays = response.data.restDays.edges.map((item) => {
            return {
              effort: "",
              calories: "",
              heartRate: "",
              date: item.node.restDayDate,
              name: "Rest day",
              type: "workout",
            };
          });

          this.activityEvents.push(...workoutLog);
          this.activityEvents.push(...restDays);

          this.activityEvents = this.activityEvents.sort((a, b) => {
            return this.$moment(a.date).isBefore(this.$moment(b.date)) ? 1 : -1;
          });

          this.nutritionEvents = response.data.nutritionLog.edges
            .map((item) => {
              return {
                date: item.node.date,
                name: item.node.meal,
                recipe: item.node.recipe,
                type: "nutrition",
              };
            })
            .sort((a, b) => {
              return this.$moment(a.date).isBefore(this.$moment(b.date)) ? 1 : -1;
            });

          this.liveEventViewEvents = response.data.userVideoLogs.edges
            .map((item) => {
              return {
                date: item.node.date,
                name: item.node.event?.name,
                instructor: `${item.node.event?.instructor.firstName} ${item.node.event?.instructor.lastName}`,
                eventType: item.node.event.eventType,
              };
            })
            .sort((a, b) => {
              return this.$moment(a.date).isBefore(this.$moment(b.date)) ? 1 : -1;
            });
        }
      },
    },
  },
};
</script>

<style>
.tracker-summary .v-expansion-panel .v-expansion-panel-content__wrap {
  padding: 0 !important;
}
</style>
